<template>
  <div :class="wrapperClasses">
    <input
      :type="type"
      :value="currentValue"
      :class="inputClasses"
      :disabled="disabled"
      :readonly="readonly"
      :placeholder="placeholder"
      v-bind="inputAttrs"
    />
    <div
      v-if="showInvalidFeedback"
      class="invalid-feedback d-block"
    >
      {{ invalidFeedback }}
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'HdFormInput',

  inheritAttrs: false,

  props: {
    value: {
      default: null
    },
    modelValue: {
      default: undefined
    },
    type: {
      type: String,
      default: 'text'
    },
    placeholder: {
      type: String,
      default: null
    },
    size: {
      type: String,
      default: null,
      validator: (value) => value == null || ['sm', 'lg'].includes(value)
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    state: {
      type: [Boolean, String],
      default: null
    },
    invalidFeedback: {
      type: String,
      default: null
    },
    debounce: {
      type: [Number, String],
      default: 0
    }
  },

  emits: ['input', 'update:modelValue', 'change'],

  created() {
    const delay = Number(this.debounce) || 0;

    if (delay <= 0) {
      this.emitValue = (value) => this.emitInput(value);
      return;
    }

    let timeoutId = null;

    this.emitValue = (value) => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        timeoutId = null;
        this.emitInput(value);
      }, delay);
    };

    this.cancelEmitValue = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = null;
    };
  },

  beforeUnmount() {
    this.cancelEmitValue?.();
  },

  computed: {
    currentValue() {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },

    wrapperClasses() {
      return [this.$attrs.class].flat().filter(Boolean);
    },

    inputClasses() {
      const classes = ['form-control'];

      if (this.size === 'sm') classes.push('form-control-sm');
      if (this.size === 'lg') classes.push('form-control-lg');
      if (this.state === true) classes.push('is-valid');
      if (this.state === false) classes.push('is-invalid');

      return classes;
    },

    showInvalidFeedback() {
      return this.state === false && this.invalidFeedback;
    },

    // In Vue 3, listeners live in $attrs (as onInput/onChange/...), so a single
    // v-bind forwards both attributes and events. We strip class (handled by
    // the wrapper) and the emits we manage ourselves, then re-add native
    // handlers: onInput drives v-model, onChange re-emits the element value.
    inputAttrs() {
      const {
        class: _class,
        onInput: _onInput,
        onChange: _onChange,
        'onUpdate:modelValue': _onUpdateModelValue,
        ...attrs
      } = this.$attrs;

      attrs.onInput = (event) => {
        this.emitValue(event.target.value);
      };

      attrs.onChange = (event) => {
        this.$emit('change', event.target.value);
      };

      return attrs;
    }
  },

  methods: {
    emitInput(value) {
      this.$emit('input', value);
      this.$emit('update:modelValue', value);
    }
  }
});
</script>
